import React, {
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  forwardRef,
  useEffect,
} from "react";
import ReactDOMServer from "react-dom/server";
import { OrgChart } from "d3-org-chart";
import CustomExpandButton from "./ChartButton/customExpandButton";
import CustomNodeContent from "./ChartNode/CustomNode";
import FitButton from "./ChartButton/fitButton";
import SwapButton from "./ChartButton/swapButton";
import SetCenteredButton from "./ChartButton/setCentered";
import * as d3 from "d3";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import EmployeeDetailsCard from "./ChartNode/employeeDetailsCard";
import EditButton from "./ChartButton/editButton";
import PDFExportButton from "./ChartButton/PDFExportButton";
import SaveButton from "./ChartButton/saveButton";
import AddNodeModal from "./modal/addNodeModal";
import MoveNodeModal from "./modal/moveNodeModal";
import { Add, Delete, Edit, MoveDown, UnfoldMore } from "@mui/icons-material";
import EditNodeModal from "./modal/editNodeModal";
import DeleteNodeModal from "./modal/deleteNodeModal";
import AlertPopup from "./modal/alertPopup";
import { I18n } from "aws-amplify/utils";
import ConfirmSaveModal from "./modal/confirmSaveSimulationModal";
import ConfirmSaveSimulationModal from "./modal/confirmSaveSimulationModal";
import ExpandAllButton from "./ChartButton/expandAllButton";
import CollapseAllButton from "./ChartButton/collapseAllButton";
import { hrPostSimulationChanges } from "../apiService";
import dayjs from "dayjs";
import ConfirmEditSimulationModal from "./modal/confirmEditSimulationModal";
import FullscreenLoader from "components/FullscreenLoader";
import { loadSimulationData, saveSimulationData } from "../indexedDbUtils";

const ChartFromSimulation = forwardRef((props, ref) => {
  const { data, simulationId, period } = props;
  const d3Container = useRef(null); //riferimento al container del grafico
  const chartRef = useRef(new OrgChart()); //riferimento per l'istanza di OrgChart
  const [cardShow, setCardShow] = useState(false);
  const [employeeId, setEmployeeId] = useState("");
  const [chartError, setChartError] = useState(false);
  // const [layoutIndex, setLayoutIndex] = useState(1); //per far partire da "top"
  const [layoutIndex, setLayoutIndex] = useState(() => {
    const saved = localStorage.getItem("orgChartLayoutIndex");
    return saved !== null ? parseInt(saved, 10) : 1;
  }); //per far partire da "top"
  const [isEditMode, setIsEditMode] = useState(true);
  const [modifiedNodeIds, setModifiedNodeIds] = useState([]); //array contenente gli id dei nodi modificati tramite edit
  const [addedNodeIds, setAddedNodeIds] = useState([]);
  const [orgData, setOrgData] = useState([]); //stato che contiene i dati del grafico

  const [contextMenu, setContextMenu] = useState(null); //stato per il menu
  const [selectedNode, setSelectedNode] = useState(null); //nodo selezionato

  //stati per le modali
  const [openAddModal, setOpenAddModal] = useState(false); //stato apertura modal add
  const [openMoveModal, setOpenMoveModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [confirmEditOpenModal, setConfirmEditOpenModal] = useState(false);

  //funzione per chiudere il menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // const nextIdRef = useRef(
  //   Math.max(0, ...data.map((n) => (typeof n.id === "number" ? n.id : 0))) + 1
  // );

  const showAlert = (message, severity) => {
    setAlertTitle(severity);
    setAlertMessage(message);
    setOpenAlert(true);
    setTimeout(() => {
      setOpenAlert(false);
    }, 3000);
  };

  const handleAddNode = async (newNode) => {
    const updatedData = [...orgData, newNode];
    setOrgData(updatedData);
    setModifiedNodeIds((prev) => [...new Set([...prev, newNode.id])]);
    // localStorage.setItem("simulationData", JSON.stringify(updatedData));
    await saveSimulationData(updatedData);
    showAlert("Node added successfully", "Success");
  };

  useEffect(() => {
    const fetchData = async () => {
      const saved = await loadSimulationData();
      if (saved) {
        setOrgData(saved || []);
      }
    };
    fetchData();
  }, []);

  const handleOpenAddModal = () => {
    if (contextMenu?.nodeData) {
      setSelectedNode(contextMenu.nodeData); //nodo genitore
    }
    setOpenAddModal(true);
    handleCloseContextMenu();
  };

  const handleOpenMoveNode = () => {
    setOpenMoveModal(true);
    handleCloseContextMenu();
  };

  const handleOpenDeleteModal = () => {
    if (!contextMenu?.nodeData) {
      //console.log("nessun nodo selezionato");
      return;
    }
    const nodeToDelete = contextMenu.nodeData;

    const hasChildren = orgData.some((n) => n.parentId === nodeToDelete.id);

    if (hasChildren) {
      setOpenAlert(true);
      setAlertTitle(I18n.get("Action not permitted"));
      setAlertMessage(
        I18n.get(
          "Please, remove all subordinate nodes before proceeding with the deletion"
        )
      );
      handleCloseContextMenu();
      return;
    }
    setSelectedNode(nodeToDelete);
    setOpenDeleteModal(true);
    handleCloseContextMenu();
  };

  const handleDeleteNode = async () => {
    if (!selectedNode) {
      //console.log("nessun nodo selezionato");
      return;
    }
    const hasChildren = orgData.some(
      (node) => node.parentId === selectedNode.id
    );
    if (hasChildren) {
      alert("questo nodo non può essere eliminato");
      setOpenDeleteModal(false);
      return;
    } else {
      const updatedData = orgData.filter((node) => node.id !== selectedNode.id);
      setOrgData(updatedData);
      // localStorage.setItem("simulationData", JSON.stringify(updatedData));
      await saveSimulationData(updatedData);
      showAlert("Node deleted successfully", "Success");
      setOpenDeleteModal(false);
      setSelectedNode(null);
    }
  };
  const handleOpenEditModal = () => {
    if (!contextMenu?.nodeData) {
      //console.log("nessun nodo selezionato");
      return;
    }
    setOpenEditModal(true);
    setTimeout(() => {
      handleCloseContextMenu();
    }, 0);
    const node = contextMenu.nodeData; //creo un nuovo oggetto per forzare l'aggiornamento
    setSelectedNode(node);
    //console.log("selectedNode impostato a: ", node);
  };

  const handleEditSubmit = async (updatedNode) => {
    setOrgData((prevData) =>
      prevData.map((node) =>
        node.id === selectedNode.id
          ? { ...node, ...updatedNode, isModified: true }
          : node
      )
    );
    setModifiedNodeIds((prev) => [...new Set([...prev, selectedNode.id])]);
    const updated = orgData.map((node) =>
      node.id === selectedNode.id ? { ...node, ...updatedNode } : node
    );
    await saveSimulationData(updated);
    // localStorage.setItem("simulationData", JSON.stringify(updated));
    showAlert("Node updated successfully", "Success");
    setOpenEditModal(false);
  };

  //funzione per aggiornare i dati dopo l'aggiunta di un nodo
  const handleSubmitAdd = (newNode) => {
    setOrgData((prevData) => [...prevData, newNode]);
    setOpenAddModal(false);
  };

  const handleMove = async (nodeId, newParentId) => {
    const updatedData = orgData.map((node) =>
      node.id === nodeId
        ? { ...node, parentId: newParentId, isModified: true }
        : node
    );
    setOrgData(updatedData);
    setModifiedNodeIds((prev) => [...new Set([...prev, nodeId])]);
    await saveSimulationData(updatedData);
    // localStorage.setItem("simulationData", JSON.stringify(updatedData));
    showAlert("Node moved successfully", "Success");
    setOpenMoveModal(false);
  };

  const handleShow = () => setCardShow(true);
  const handleClose = () => setCardShow(false);

  const handleEditClick = () => {
    setIsEditMode((prevMode) => !prevMode); //toggle
  };

  const handleOpen = async () => {
    //quando clicco sul pulsante apro la modal
    // setIsEditMode(false);
    setConfirmEditOpenModal(true); //apro il popup
  };
  const [highlightedUser, setHighlightedUser] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const handleEditSimulation = async (formData) => {
    setFullLoading(true);
    try {
      // const cardContent = JSON.parse(localStorage.getItem("simulationData"));
      const cardContent = await loadSimulationData();
      //   const lastUpdate=dayjs().format("YYYY-MM-DD HH:mm:ss");
      const payload = {
        // last_update:lastUpdate,
        ...formData, //campi non annidati
        card_content: cardContent,
      };
      await hrPostSimulationChanges(simulationId, payload);
      //salvo i dati del grafico editato
      showAlert(I18n.get("Simulation updated successfully"), "Success");
      setIsEditMode(false);
    } catch (error) {
      //console.log(error);
    } finally {
      setFullLoading(false);
    }
    // console.log("test: ", localStorage.getItem("simulationData"));
  };

  const handleFit = (event) => {
    if (chartRef.current) {
      chartRef.current.fit(event);
    }
  };

  const handleSwapLayout = useCallback((event) => {
    const nextIndex = (layoutIndex + 1) % 4;
    setLayoutIndex(nextIndex);
    localStorage.setItem("orgChartLayoutIndex", nextIndex);
    return nextIndex;
  });

  const handleExpandAll = () => {
    if (chartRef.current) {
      chartRef.current.expandAll().render();
    }
  };

  const handleCollapseAll = () => {
    if (chartRef.current) {
      chartRef.current.collapseAll().render();
    }
  };

  // const handleSetCentered = useCallback(() => {
  //   if (chartRef.current) {
  //     const id = props.inputID;
  //     console.log(id);
  //     chartRef.current.clearHighlighting();
  //     chartRef.current
  //       .setCentered(id)
  //       .setHighlighted(id)
  //       .setUpToTheRootHighlighted(id)
  //       .render();
  //   }
  // });

  const handleClearHighlighting = (event) => {
    event.preventDefault();
    if (chartRef.current) {
      chartRef.current.clearHighlighting();
    }
  };

  useLayoutEffect(() => {
    const toggleDetailsCard = (nodeId) => {
      handleShow();
      setEmployeeId(nodeId);
    };

    const handleLayout = () => handleSwapLayout();

    if (data && d3Container.current) {
      try {
        chartRef.current
          .container(d3Container.current)
          .data(orgData) //data
          .nodeWidth((d) => 300)
          .nodeHeight((d) => 185)
          .layout(["left", "top", "right", "bottom"][layoutIndex])
          .compactMarginBetween((d) => 80)
          .onNodeClick((d) => {
            toggleDetailsCard(d.id);
          })
          .linkUpdate(function (d, i, arr) {
            //colore dei link
            d3.select(this).attr("stroke", "#00449e");
          })
          .buttonContent((node) => {
            return ReactDOMServer.renderToStaticMarkup(
              <CustomExpandButton {...node.node} />
            );
          })
          .nodeContent((d) => {
            return ReactDOMServer.renderToStaticMarkup(
              <div data-id={d.id} className="node-content">
                <CustomNodeContent
                  {...d}
                  orgData={orgData}
                  modifiedNodeIds={modifiedNodeIds}
                />
              </div>
            );
          })
          .render();
      } catch (error) {
        setChartError(true);
        console.log(error)
      }
    }

    // setTimeout(()=>{
    //   if(d3Container.current){
    //     const svg=d3Container.current.querySelector("svg");
    //     if(svg){
    //       svg.style.backgroundImage= `url("/bg_chart.png")`;
    //       svg.style.backgroundSize="cover";
    //       svg.style.backgroundPosition="center";
    //       svg.style.backgroundRepeat="no-repeat";
    //       svg.style.width="100%";
    //       svg.style.height="100%";
    //     }
    //   }
    // }, 500);
  }, [orgData, layoutIndex]);

  useEffect(() => {
    if (!d3Container.current) {
      //console.log("d3container.current è null");
      return;
    }

    setTimeout(() => {
      const container = d3Container.current;
      if (!container) {
        //console.log("d3Container.current è null");
        return;
      }

      const svg = container.querySelector("svg");
      if (!svg) {
        //console.log("svg non trovato dentro il container");
        return;
      }
      //funzione per aprire il menu da tasto destro
      const handleNodeRightClick = (event) => {
        event.preventDefault();
        if (!isEditMode) return;

        const node = event.target.closest(".node-content");
        if (!node) {
          //console.log("nessun nodo sotto il cursore");
          return;
        }

        const nodeId = node.getAttribute("data-id");
        //console.log("click destro sul nodo con ID: ", nodeId);

        const nodeData = orgData.find((n) => n.id === nodeId);
        if (!nodeData) {
          //console.log("nodo non trovato nei dati");
          return;
        }
        setSelectedNode(nodeData); //imposta il nodo genitore

        setContextMenu({
          mouseX: event.clientX - 2,
          mouseY: event.clientY - 4,
          nodeData,
        });
      };
      svg.addEventListener("contextmenu", handleNodeRightClick);
      //console.log("listner attaccato al grafico");
      return () => {
        svg.removeEventListener("contextmenu", handleNodeRightClick);
        //console.log("listner rimosso");
      };
    }, 1000);
  }, [orgData, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setContextMenu(null);
    }
  }, [isEditMode]);
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        90;
      setTableHeight(availableHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  return (
    <>
      <FullscreenLoader loading={fullLoading} />
      <Stack
        display="flex"
        direction="row"
        justifyContent="center"
        width="100%"
        gap={3}
        padding="1rem"
        position="relative"
      >
        <ExpandAllButton onExpand={handleExpandAll} />
        <CollapseAllButton onCollapse={handleCollapseAll} />
        <PDFExportButton
          chartRef={d3Container}
          onFitChart={handleFit}
          period={period}
          fileName={"Simulation"}
          setFullLoading={setFullLoading}
        />
        <FitButton onFitChart={handleFit} ref={d3Container} />
        <SwapButton onSwapLayout={handleSwapLayout} ref={d3Container} />
        <EditButton onEdit={handleEditClick} isEditMode={isEditMode} />
        {/* <FormControlLabel control={<Switch checked={isEditMode} onChange={(e)=>setIsEditMode(e.target.checked)} color="primary"/>}
        label="Edit mode"/> */}
        {isEditMode && <SaveButton open={handleOpen} />}
        {/* <SetCenteredButton
          centerChart={handleSetCentered}
          clearHighlighting={handleClearHighlighting}
          ref={d3Container}
        /> */}
      </Stack>
      {chartError ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            minHeight: tableHeight,
          }}
        >
          <Typography
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
          >
            {I18n.get(
              "The chart could not be created because of invalid or incomplete data."
            )}
          </Typography>
        </Box>
      ) : (
        <div
          ref={(node) => {
            d3Container.current = node;
            if (ref) ref.current = node;
          }}
        />
      )}
      {isEditMode && contextMenu !== null && (
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenAddModal}>
            <Add color="success" /> {I18n.get("Add node")}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenEditModal}>
            <Edit color="info" /> {I18n.get("Edit node")}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenMoveNode}>
            <MoveDown color="primary" />
            {I18n.get("Move node")}{" "}
          </MenuItem>
          <MenuItem sx={{ gap: 1 }} onClick={handleOpenDeleteModal}>
            {" "}
            <Delete color="error" />
            {I18n.get("Delete node")}{" "}
          </MenuItem>
        </Menu>
      )}

      <ConfirmEditSimulationModal
        open={confirmEditOpenModal}
        onClose={() => setConfirmEditOpenModal(false)}
        onSubmit={handleEditSimulation}
      />

      <AddNodeModal
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          setSelectedNode(null);
        }}
        onSubmit={handleAddNode}
        parentNode={selectedNode}
      />

      <MoveNodeModal
        open={openMoveModal}
        onClose={() => setOpenMoveModal(false)}
        nodes={orgData}
        selectedNode={selectedNode}
        onMoveNode={handleMove}
      />
      <EditNodeModal
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        selectedNode={selectedNode} //nodo da modificare
        onSubmit={handleEditSubmit}
        orgData={data}
      />
      <DeleteNodeModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        selectedNode={selectedNode}
        onSubmit={handleDeleteNode}
      />
      <AlertPopup
        open={openAlert}
        onClose={() => setOpenAlert(false)}
        alertTitle={alertTitle}
        alertMessage={alertMessage}
      />
      {/* {cardShow && (
          <EmployeeDetailsCard
            employees={data}
            employee={data.find((employee) => employee.id === employeeId)}
            handleClose={handleClose}
          />
        )} */}
    </>
  );
});

export default ChartFromSimulation;
